import React, { useState, useRef, useEffect } from "react";
import { Offcanvas, Dropdown, OverlayTrigger } from "react-bootstrap";
import { getBackgroundColor, formatSmartDecimal, getTokenSymbol, formatNumbers } from "../../utils/helpers";
import { EVIDENCE_MAX_FILE_SIZE, EVIDENCE_MAX_ACCUMULATED_SIZE } from "../../utils/constants";
import { notifyError } from "../Toast";
import { DEFAULT_ERROR_HEADING } from "../../utils/ConstantMessages";
import "./disputeoffcanvasmobile.scss";
import { useSwipeClose } from "../../hooks/useSwipeClose";
import TooltipCustom from "react-bootstrap/Tooltip";

const WarningIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 003.54 21H20.46A2 2 0 0022.18 18L13.71 3.86A2 2 0 0010.29 3.86Z"
      stroke="#E8705A"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="9" stroke="#999" strokeWidth="1.5" />
    <path
      d="M10 9V14"
      stroke="#999"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="10" cy="6.5" r="0.75" fill="#999" />
  </svg>
);

const UploadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 4V13M10 4L7 7M10 4L13 7"
      stroke="#999"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 14V15C3 16.1046 3.89543 17 5 17H15C16.1046 17 17 16.1046 17 15V14"
      stroke="#999"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5"
      stroke="#999"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const MAX_FILES = 3;

const DisputeOffcanvas = ({
  show,
  onHide,
  poolsdetail,
  winnerData,
  proposedOutcome,
  setProposedOutcome,
  title,
  setTitle,
  description,
  setDescription,
  files,
  setFiles,
  bondAmount,
  gasEstimateInUsdt,
  gasFeesSpinner,
  handleShowCreateDispute,
  theme,
  tokenSymbol,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (show && poolsdetail?.options?.length > 0 && !proposedOutcome) {
      setProposedOutcome(poolsdetail.options[0]);
    }
  }, [show, poolsdetail?.options]);

  const winnerDotColor = getBackgroundColor(
    poolsdetail?.options?.findIndex((opt) => opt._id === winnerData?.winnerId),
    theme,
    poolsdetail?.options?.length
  );

  const selectedDotColor = getBackgroundColor(
    poolsdetail?.options?.findIndex((opt) => opt._id === proposedOutcome?._id),
    theme,
    poolsdetail?.options?.length
  );

  const handleFileChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const selectedFiles = Array.from(e.target.files);

    if ((files?.length ?? 0) >= MAX_FILES) {
      notifyError(DEFAULT_ERROR_HEADING, "Maximum 3 evidence files allowed");
      e.target.value = "";
      return;
    }

    const oversized = selectedFiles.find((f) => f.size > EVIDENCE_MAX_FILE_SIZE);
    if (oversized) {
      notifyError(DEFAULT_ERROR_HEADING, `"${oversized.name}" exceeds the 7 MB per-file limit`);
      e.target.value = "";
      return;
    }

    const remaining = MAX_FILES - (files?.length ?? 0);
    const filesToAdd = selectedFiles.slice(0, remaining);
    if (selectedFiles.length > remaining) {
      notifyError(DEFAULT_ERROR_HEADING, `Maximum 3 evidence files allowed. Only ${remaining} file(s) added.`);
    }

    const currentTotalSize = (files ?? []).reduce((sum, f) => sum + f.size, 0);
    const newTotalSize = currentTotalSize + filesToAdd.reduce((sum, f) => sum + f.size, 0);
    if (newTotalSize > EVIDENCE_MAX_ACCUMULATED_SIZE) {
      notifyError(DEFAULT_ERROR_HEADING, "Total evidence size cannot exceed 25 MB");
      e.target.value = "";
      return;
    }

    setFiles([...(files ?? []), ...filesToAdd]);
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setFiles((files ?? []).filter((_, i) => i !== index));
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleSubmit = () => {
    handleShowCreateDispute();
  };

  const handleClose = () => {
    setTitle("");
    setDescription("");
    setFiles([]);
    onHide();
  };

  const swipeRef = useSwipeClose(show, handleClose);

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="bottom"
      className="dispute-offcanvas new-offcanvas-style-mobile"
    >
      <Offcanvas.Header ref={swipeRef}>
        <span className='mobile-swap-close d-none'></span>
      </Offcanvas.Header>
      <Offcanvas.Body className="dispute-offcanvas__body">
        <div className="dispute-offcanvas__icon-wrapper">
          <div className="dispute-offcanvas__icon">
            <WarningIcon />
          </div>
        </div>

        <h2 className="dispute-offcanvas__title">
          You are about to create a dispute
        </h2>
        <p className="dispute-offcanvas__subtitle">
          Select the correct outcome or flag for early closure.
        </p>

        {/* Proposed Outcome */}
        <div className="dispute-offcanvas__proposed">
          <div className="dispute-offcanvas__proposed-left">
            <span className="dispute-offcanvas__proposed-label">
              PROPOSED OUTCOME
            </span>
            <div className="dispute-offcanvas__proposed-value">
              <span
                className="dispute-offcanvas__dot"
                style={{ backgroundColor: winnerDotColor }}
              />
              {winnerData?.winnerOption || "—"}
            </div>
          </div>
          <span className="dispute-offcanvas__disputing-badge">DISPUTING</span>
        </div>

        {/* <label className="dispute-offcanvas__select-label">
          Select Outcome
        </label>
        <Dropdown className="dispute-offcanvas__dropdown">
          <Dropdown.Toggle
            variant="none"
            className="dispute-offcanvas__dropdown-toggle"
          >
            <div className="dispute-offcanvas__dropdown-value">
              <span
                className="dispute-offcanvas__dot"
                style={{ backgroundColor: selectedDotColor }}
              />
              {proposedOutcome?.optionName || "Select outcome"}
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu className="dispute-offcanvas__dropdown-menu">
            {poolsdetail?.options?.map((option, index) => {
              const bg = getBackgroundColor(index, theme, poolsdetail?.options?.length);
              return (
                <Dropdown.Item
                  key={option._id || index}
                  className="dispute-offcanvas__dropdown-item"
                  active={proposedOutcome?._id === option._id}
                  onClick={() => setProposedOutcome(option)}
                >
                  <span
                    className="dispute-offcanvas__dot"
                    style={{ backgroundColor: bg }}
                  />
                  {option.optionName}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown> */}

        <div className="option-field">
          <label>Dispute Title</label>
          <input type="text" placeholder='Dispute Title' value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="option-field">
          <label>Dispute Description</label>
          <textarea placeholder='Dispute Description' value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className="dispute-offcanvas__evidence-label">
          <span>Upload Evidence</span>
          <span className="dispute-offcanvas__required">*</span>
        </div>
        <p className="after-upload-text">
          You must provide at least one file of evidence
        </p>

        {(files?.length ?? 0) > 0 && (
          <div className="dispute-offcanvas__file-list">
            {files.map((file, index) => (
              <div key={index} className="dispute-offcanvas__file-item">
                <div className="dispute-offcanvas__file-item-left">
                  <UploadIcon />
                  <span className="dispute-offcanvas__file-name">
                    {file.name}
                  </span>
                </div>
                <button
                  className="dispute-offcanvas__file-remove"
                  onClick={() => handleRemoveFile(index)}
                >
                  <CloseIcon />
                </button>
              </div>
            ))}
          </div>
        )}

        {(files?.length ?? 0) < MAX_FILES && (
          <div
            className="dispute-offcanvas__file-upload"
            onClick={handleBrowseClick}
          >
            <div className="dispute-offcanvas__file-left">
              <UploadIcon />
              <span className="dispute-offcanvas__file-placeholder">
                Choose a file...
              </span>
            </div>
            <button
              className="dispute-offcanvas__browse-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleBrowseClick();
              }}
            >
              Browse
            </button>
          </div>
        )}

        <input
          type="file"
          ref={fileInputRef}
          accept=".pdf,.png,.jpg,.jpeg,.csv,.json"
          onChange={handleFileChange}
          hidden
        />

        {
          (files?.length ?? 0) < MAX_FILES && (
            <p className="dispute-offcanvas__file-hint">
              Up to 3 files - PDF, images, CSV, JSON
            </p>
          )
        }




        <div className="dispute-offcanvas__fees">
          <div className="dispute-offcanvas__fee-row dispute-offcanvas__fee-row--bond">
            <div className="dispute-offcanvas__fee-label">
              Dispute Bond <OverlayTrigger
                placement="top"
                overlay={
                  <TooltipCustom className="liquidity-provided-tooltip" id="tooltip-1">
                    <p>You will be charged with 0.1% bond fee of the market's volume
                      - the minimum amount is 25$ and the maximum is 500$</p>
                  </TooltipCustom>
                }
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <g clipPath="url(#clip0_10136_49160)">
                    <path d="M7.00008 12.8332C10.2217 12.8332 12.8334 10.2215 12.8334 6.99984C12.8334 3.77818 10.2217 1.1665 7.00008 1.1665C3.77842 1.1665 1.16675 3.77818 1.16675 6.99984C1.16675 10.2215 3.77842 12.8332 7.00008 12.8332Z" stroke="#9F9F9F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7 9.33333V7" stroke="#9F9F9F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7 4.6665H7.01" stroke="#9F9F9F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_10136_49160">
                      <rect width="14" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </OverlayTrigger>
            </div>
            <span className="dispute-offcanvas__fee-value">
              {getTokenSymbol(tokenSymbol)}{formatNumbers(+bondAmount) || "—"}
            </span>
          </div>
          <div className="dispute-offcanvas__fee-row dispute-offcanvas__fee-row--network">
            <span className="dispute-offcanvas__fee-label" style={{ color: "var(--para)" }}>Network Fee</span>
            <span className="dispute-offcanvas__fee-value" style={{ color: "var(--para)" }}>
              {gasFeesSpinner
                ? <span className="spinner" />
                : `$${formatSmartDecimal(gasEstimateInUsdt) || "—"}`}
            </span>
          </div>
        </div>
        <p className="dispute-offcanvas__refund-note">
          Bond refunded if dispute is successful
        </p>

        <button
          className="dispute-offcanvas__submit-btn"
          onClick={handleSubmit}
          disabled={gasFeesSpinner || !title || !description || (files?.length ?? 0) === 0}
        >
          {gasFeesSpinner ? "Loading..." : "SUBMIT PROPOSAL"}
        </button>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default DisputeOffcanvas;
